import { AxiosResponse } from 'axios';

import axiosInstance, { commaParamsSerializer } from 'api/base';
import { normalizeBaseGist, normalizePageData } from 'utils/commonNormalizer';
import { normalizeSearchFiltersPayload } from 'utils/filters';
import { prepareFormData } from 'utils/form';
import { IAllPageFilters, IBaseGist, INullable, IPageType } from 'utils/types';

import {
  mapSingleChangeRuleToForm,
  normalizeBulkCopyChangeRulesResult,
  normalizeBulkCreateResult,
  normalizeChangeRule,
  normalizeChangeRulePayload,
  normalizeCpmNames,
  normalizeSingleChangeRule,
} from './normalizers';
import {
  IBulkCopyChangeRulesPayload,
  IBulkCopyChangeRulesResult,
  IBulkCreateChangeRulesPayload,
  IBulkCreateChangeRulesResult,
  IChangeRule,
  IChangeRuleData,
  IChangeRuleForm,
  IChangeRulesGroupEdit,
  ISingleChangeRule,
  IToggleChangeRulePayload,
  IUpdateChangeRulePayload,
} from './types';

const IChangeRuleApi = {
  async bulkCopyChangeRules(
    payload: IBulkCopyChangeRulesPayload,
  ): Promise<IBulkCopyChangeRulesResult> {
    const response = await axiosInstance.post('change-rules/bulk-copy', payload);

    return normalizeBulkCopyChangeRulesResult(response?.data);
  },

  async bulkCreateChangeRules(
    payload: IBulkCreateChangeRulesPayload,
  ): Promise<IBulkCreateChangeRulesResult> {
    const response = await axiosInstance.post(
      'change-rules/bulk-create',
      normalizeChangeRulePayload(payload),
    );

    return normalizeBulkCreateResult(response?.data);
  },

  async createChangeRule(payload: IChangeRuleForm): Promise<IBaseGist> {
    const result = await axiosInstance.post(
      'change-rules',
      normalizeChangeRulePayload(payload),
    );
    return normalizeBaseGist(result?.data);
  },

  async deleteChangeRule(id: number): Promise<AxiosResponse> {
    const response = await axiosInstance.delete(`change-rules/${id}`);
    return response.data;
  },

  async getAllChangeRules(filters: IAllPageFilters): Promise<IPageType<IChangeRule>> {
    const normalizedFilter = normalizeSearchFiltersPayload(filters);

    const response = await axiosInstance.get('change-rules', {
      params: normalizedFilter,
    });

    return normalizePageData<IChangeRuleData, IChangeRule>(
      response.data,
      normalizeChangeRule,
      JSON.stringify(filters.search),
    );
  },

  // Only names present on every selected terminal can be resolved by the batch.
  async getBulkCreateCpmNames(merchantTerminalIds: number[]): Promise<string[]> {
    const response = await axiosInstance.get(
      'change-rules/bulk-create/cashier-payment-methods',
      { params: { merchantTerminalIds }, paramsSerializer: commaParamsSerializer },
    );

    return normalizeCpmNames(response?.data);
  },

  async getChangeRule(id: string): Promise<ISingleChangeRule> {
    const response = await axiosInstance.get(`change-rules/${id}`);
    return normalizeSingleChangeRule(response?.data);
  },

  // Takes the same `search` as the group edit PATCH.
  async getChangeRulesCpmNames(filters: IAllPageFilters): Promise<string[]> {
    const { search } = normalizeSearchFiltersPayload(filters);

    const response = await axiosInstance.get('change-rules/cashier-payment-methods', {
      params: { search },
    });

    return normalizeCpmNames(response?.data);
  },

  async groupEdit({
    filters,
    settings,
  }: IChangeRulesGroupEdit): Promise<INullable<number>> {
    const { search } = normalizeSearchFiltersPayload(filters);

    const response = await axiosInstance.patch(
      'change-rules',
      prepareFormData(settings),
      { params: { search } },
    );

    return response.data.result || null;
  },

  async toggleChangeRuleEnabled({
    enabled,
    id,
  }: IToggleChangeRulePayload): Promise<IBaseGist> {
    const current = await axiosInstance.get(`change-rules/${id}`);
    const rule = normalizeSingleChangeRule(current?.data);

    return IChangeRuleApi.updateChangeRule({
      id,
      payload: { ...mapSingleChangeRuleToForm(rule), enabled },
    });
  },

  async updateChangeRule({ id, payload }: IUpdateChangeRulePayload): Promise<IBaseGist> {
    const result = await axiosInstance.put(
      `change-rules/${id}`,
      normalizeChangeRulePayload(payload),
    );
    return normalizeBaseGist(result?.data);
  },
};

export default IChangeRuleApi;
